import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { connect } from 'react-redux'
import ScrollableTable from './ScrollableTable'
import FontNumber from '../common/FontNumber'
import { formatNumber, decimalPlaces, roundValue } from '../../utils/number'
import { withI18n } from '../../i18n/util'
import { getLastPrice } from '../../containers/balances'

const Header = styled.div`
  height: 30px;
  padding: 0 15px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  background: ${({ theme }) => theme.exchangeFilterBg};
`

const EstimatedValue = styled.div`
  color: #848e9c;
  font-size: 12px;
`

const Content = styled.div`
  margin-top: 10px;
  height: 200px;
  padding: 0 6px;
  overflow: hidden;
  position: relative;
  .table-wrap {
    .fixed-table {
      bottom: 20px;
    }
  }
  table tbody tr {
    padding: 0;
    height: 25px;
    line-height: 25px;
    &:nth-child(odd) {
      background: ${({ theme }) => theme.tableOddRowBg};
    }
  }
`

const ValueColor = styled.div`
  color: #848e9c;
  text-align: left;
`

const CellLeft = styled(ValueColor)`
  padding: 0 5px;
  text-align: left;
`
class UserFounds extends PureComponent {
  static propTypes = {
    pairs: PropTypes.array,
    tokens: PropTypes.array,
    balances: PropTypes.array,
    symbolTickers: PropTypes.array,
    cryptoCurrencyRate: PropTypes.object,
    formatI18nText: PropTypes.func,
  }

  static defaultProps = {
    pairs: [],
    tokens: [],
    balances: [],
    symbolTickers: [],
    cryptoCurrencyRate: {},
    formatI18nText: () => {},
  }

  _buildColumns = () => {
    const { formatI18nText } = this.props
    const columns = [
      {
        title: formatI18nText('exchange.asset'),
        dataIndex: 'asset',
        headerAlign: 'left',
        key: 'Asset',
        width: '10%',
        render: item => <CellLeft>{item.displayName}</CellLeft>,
      },
      {
        title: formatI18nText('exchange.name'),
        headerAlign: 'left',
        dataIndex: 'name',
        key: 'Name',
        width: '20%',
        render: item => <ValueColor>{item.name}</ValueColor>,
      },
      {
        title: formatI18nText('exchange.totalBalance'),
        headerAlign: 'left',
        dataIndex: 'totalBalance',
        key: 'TotalBalance',
        width: '15%',
        render: item => (
          <ValueColor>
            <FontNumber value={formatNumber(item.totalBalance, decimalPlaces(item.totalBalance))} />
          </ValueColor>
        ),
      },
      {
        title: formatI18nText('exchange.availableBalance'),
        headerAlign: 'left',
        dataIndex: 'availableBalance',
        key: 'AvailableBalance',
        width: '15%',
        render: item => (
          <ValueColor>
            <FontNumber
              value={formatNumber(item.availableBalance, decimalPlaces(item.availableBalance))}
            />
          </ValueColor>
        ),
      },
      {
        title: formatI18nText('exchange.frozen'),
        headerAlign: 'left',
        dataIndex: 'frozen',
        key: 'Frozen',
        width: '10%',
        render: item => (
          <ValueColor>
            <FontNumber value={formatNumber(item.frozen, decimalPlaces(item.frozen))} />
          </ValueColor>
        ),
      },
      {
        title: formatI18nText('exchange.inOrder'),
        headerAlign: 'left',
        dataIndex: 'inOrder',
        key: 'InOrder',
        width: '15%',
        render: item => (
          <ValueColor>
            <FontNumber value={formatNumber(item.inOrder, decimalPlaces(item.inOrder))} />
          </ValueColor>
        ),
      },
      {
        title: formatI18nText('exchange.BTCValue'),
        headerAlign: 'left',
        dataIndex: 'BTCValue',
        key: 'BTCValue',
        width: '15%',
        render: item => (
          <ValueColor>
            <FontNumber value={formatNumber(item.BTCValue, decimalPlaces(item.BTCValue))} />
          </ValueColor>
        ),
      },
    ]
    return columns
  }

  _buildBalances = () => {
    const { tokens, balances, symbolTickers, pairs, cryptoCurrencyRate } = this.props

    const result = balances.map(balance => {
      const token = tokens.find(item => balance.symbol === item.symbol) || {}
      const { symbol } = token

      // incase balances API (0.02 seconds) has a much shorter cache time than tokens (10 mins).
      if (!symbol) {
        return {
          asset: token.symbol,
          name: token.name,
          displayName: token.original_symbol,
          totalBalance: 0,
          availableBalance: 0,
          frozen: 0,
          inOrder: 0,
          BTCValue: 0,
          bnbValue: 0,
          usdtValue: 0,
        }
      }

      let availableBalance = 0
      let inOrder = 0
      let frozen = 0
      if (balance) {
        availableBalance = parseFloat(balance.free, 10)
        inOrder = parseFloat(balance.locked, 10)
        frozen = parseFloat(balance.frozen, 10)
        availableBalance = Number.isNaN(availableBalance) ? 0 : availableBalance
        inOrder = Number.isNaN(inOrder) ? 0 : inOrder
        frozen = Number.isNaN(frozen) ? 0 : frozen
      }

      const totalBalance = availableBalance + inOrder + frozen

      let bnbBtcPair = symbolTickers.find(st => st.symbol.includes('BNB_BTC.B'))
      if (!bnbBtcPair) {
        bnbBtcPair =
          pairs.find(
            p => p.base_asset_symbol === 'BNB' && p.quote_asset_symbol.includes('BTC.B')
          ) || {}
      }

      const lastPrice = symbol === 'BNB' ? 1 : getLastPrice(symbolTickers, pairs, symbol)

      const bnbValue = symbol === 'BNB' ? totalBalance : lastPrice * totalBalance

      let btcValue = symbol.includes('BTC.B')
        ? totalBalance
        : bnbValue * parseFloat(bnbBtcPair.price || 0, 10)
      btcValue = roundValue(btcValue)

      let usdtValue
      if (btcValue === 0 && bnbValue > 0) {
        usdtValue = cryptoCurrencyRate.BNB_USDT ? bnbValue * cryptoCurrencyRate.BNB_USDT : ''
      } else {
        usdtValue = cryptoCurrencyRate.BTC_USDT ? btcValue * cryptoCurrencyRate.BTC_USDT : ''
      }

      return {
        asset: symbol,
        name: token.name,
        displayName: token.original_symbol,
        totalBalance: totalBalance < 0.000001 ? totalBalance.toFixed(8) : totalBalance,
        availableBalance:
          availableBalance < 0.000001 ? availableBalance.toFixed(8) : availableBalance,
        frozen: frozen < 0.000001 ? frozen.toFixed(8) : frozen,
        inOrder: inOrder < 0.000001 ? inOrder.toFixed(8) : inOrder,
        BTCValue: btcValue,
        bnbValue,
        usdtValue,
      }
    })

    return result.sort((a, b) => a?.asset?.localeCompare(b.asset))
  }

  render() {
    const columns = this._buildColumns()
    const balanceSource = this._buildBalances()
    const { cryptoCurrencyRate, formatI18nText } = this.props
    let totalBTCValue = balanceSource.reduce(
      (n, pre) => parseFloat(n, 10) + parseFloat(pre.BTCValue, 10),
      0
    )

    let totalBnbValue = balanceSource.reduce((n, pre) => n + parseFloat(pre.bnbValue, 10), 0)

    totalBTCValue = roundValue(totalBTCValue)
    totalBnbValue = roundValue(totalBnbValue)

    let totalUsd = balanceSource.reduce((n, pre) => n + parseFloat(pre.usdtValue, 10), 0)

    if (cryptoCurrencyRate) {
      totalUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        totalUsd
      )
    }

    return (
      <div>
        <Header>
          <EstimatedValue>
            {formatI18nText('exchange.estimatedValue')}:
            {totalBTCValue === 0 ? (
              <>
                <FontNumber value={formatNumber(totalBnbValue, decimalPlaces(totalBnbValue))} />
                BNB /&nbsp;
              </>
            ) : (
              <>
                <FontNumber value={formatNumber(totalBTCValue, decimalPlaces(totalBTCValue))} />
                BTC /&nbsp;
              </>
            )}
            <FontNumber value={totalUsd} />
          </EstimatedValue>
        </Header>
        <Content>
          <ScrollableTable columns={columns} isFixedHeader={true} dataSource={balanceSource} />
        </Content>
      </div>
    )
  }
}

const mapStateToProps = ({
  account: {
    userInfo: { balances },
  },
  exchange: { cryptoCurrencyRate, tokens },
  tradePairs: { pairs },
  tickers: { symbolTickers },
}) => ({
  balances,
  symbolTickers,
  cryptoCurrencyRate,
  tokens,
  pairs,
})

export default withI18n(connect(mapStateToProps)(UserFounds))
